import { makeAuthenticatedRequest } from './api';

// The API returns ONE result entry per file-pair (batch id), since an upload can carry several
// pairs. The UI renders a single aggregated view, so these wrappers flatten the per-batch results
// while keeping the raw per-batch objects for commit and the batch ids for the per-batch actions.

const normalizePreview = (batches) => {
  const complete = batches.filter((b) => b && b.status === 'complete');
  const problems = batches
    .filter((b) => b && b.status !== 'complete')
    .map((b) => ({ batchId: b.batchId, status: b.status, reason: b.reason }));

  const invoices = complete.flatMap((b) => b.invoices || []);
  const rctis = complete.flatMap((b) => b.rctis || []);
  const skips = complete.flatMap((b) => b.skips || []);
  // Non-blocking notes on batches that WILL import: lone/partially-matched files, empty counterparts.
  const warnings = batches.flatMap((b) => (b?.warnings || []).map((message) => ({ batchId: b.batchId, message })));
  const sum = (pick) => complete.reduce((n, b) => n + pick(b), 0);

  return {
    batches: complete, // raw per-batch objects, passed back to /commit unchanged
    batchIds: complete.map((b) => b.batchId).filter(Boolean),
    batchId: complete.map((b) => b.batchId).filter(Boolean).join(', ') || 'N/A',
    invoices,
    rctis,
    skips,
    warnings,
    summary: {
      invoicesImported: sum((b) => b.summary?.invoicesImported ?? (b.invoices || []).filter((i) => i.ok !== false).length),
      rctisImported: sum((b) => b.summary?.rctisImported ?? (b.rctis || []).filter((r) => r.ok !== false).length),
      skipped: sum((b) => b.summary?.skipped ?? (b.skips || []).length),
    },
    problems, // lone/unpaired/empty-counterpart batches that cannot be committed
    hasComplete: complete.length > 0,
  };
};

/**
 * Uploads the paired RCTI + Xero-invoice files for a dry-run preview.
 * Pure read on the backend: parses, pairs, and resolves franchisees/contacts with ZERO writes.
 * @param {File[]} files
 * @returns {Promise<object>} aggregated preview (see normalizePreview)
 */
export const previewImport = async (files) => {
  if (!files || files.length === 0) {
    throw new Error('At least one file is required.');
  }

  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append('files', file));

  const response = await makeAuthenticatedRequest('/invoice-import/preview', {
    method: 'POST',
    body: formData,
  });

  const batches = response?.results || (Array.isArray(response) ? response : [response].filter(Boolean));
  return normalizePreview(batches);
};

/**
 * Commits the complete batches from a preview: idempotent Firestore writes (invoices/rctis + the
 * invoiceImportBatches audit record). No Xero/email calls.
 * @param {object} preview - the object returned by previewImport
 * @returns {Promise<object>} { batchIds, createdInvoiceIds, createdRctiIds, skips, perBatch }
 */
export const commitImport = async (preview) => {
  const batches = preview?.batches || [];
  if (batches.length === 0) {
    throw new Error('There are no complete batches to commit.');
  }

  const response = await makeAuthenticatedRequest('/invoice-import/commit', {
    method: 'POST',
    body: { previewResults: batches },
  });

  const results = response?.results || (Array.isArray(response) ? response : [response].filter(Boolean));
  return {
    batchIds: results.map((r) => r.batchId).filter(Boolean),
    createdInvoiceIds: results.flatMap((r) => r.createdInvoiceIds || []),
    createdRctiIds: results.flatMap((r) => r.createdRctiIds || []),
    skips: results.flatMap((r) => r.skips || []),
    perBatch: results,
  };
};

/**
 * Runs (or plans, when dryRun) the per-item actions for the committed batches: emailing RCTIs and pushing
 * invoices/RCTIs to Xero. dryRun=true returns the plan with zero side effects; preview never reaches this
 * endpoint. Sends ALL batch ids in a single request so the API pools them into one email job (avoids the
 * per-batch 'another email job is already running' clash) and so no partial client-side loop can hide
 * side effects that already happened.
 * @param {{importBatchIds: string[], actions: object, dryRun: boolean}} params
 * @returns {Promise<{dryRun: boolean, plan: object[], successes: object[], failures: object[]}>}
 */
export const executeImportActions = async ({ importBatchIds, actions, dryRun }) => {
  if (!importBatchIds || importBatchIds.length === 0) {
    throw new Error('There is no committed batch to run actions on.');
  }

  const res = await makeAuthenticatedRequest('/invoice-import/execute', {
    method: 'POST',
    body: { importBatchIds, actions, dryRun },
  }) || {};

  return {
    dryRun: !!dryRun,
    plan: res.plan || [],
    successes: res.successes || [],
    failures: res.failures || [],
  };
};

/**
 * Lists previously committed import batches (audit history).
 */
export const listImportHistory = async () => {
  return makeAuthenticatedRequest('/invoice-import/history', {
    method: 'POST',
    body: {},
  });
};

/**
 * Combined commit + execute: idempotently commits the preview batches to Firestore, then
 * immediately pushes invoices/RCTIs to Xero and emails RCTIs to franchisees.
 * Retry-safe — already-committed docs are skipped, already-pushed/emailed docs are skipped.
 * @param {object} preview - the object returned by previewImport
 * @param {boolean} [mockMode] - dev-only: commit drafts and email RCTIs but do NOT push to Xero
 * @returns {Promise<object>} { success: boolean, commitSkips, successes, failures }
 */
export const commitAndExecuteImport = async (preview, mockMode = false) => {
  const batches = preview?.batches || [];
  if (batches.length === 0) {
    throw new Error('There are no complete batches to push.');
  }

  const res = await makeAuthenticatedRequest('/invoice-import/commit-and-execute', {
    method: 'POST',
    body: {
      previewResults: batches,
      actions: { emailRctis: true, pushRctisToXero: !mockMode, pushInvoicesToXero: !mockMode }
    },
  }) || {};

  const commitResults = res.commitResults || [];
  const executeResult = res.executeResult || {};

  return {
    success: res.status === 'success',
    commitSkips: commitResults.flatMap((r) => r.skips || []),
    createdInvoiceIds: commitResults.flatMap((r) => r.createdInvoiceIds || []),
    createdRctiIds: commitResults.flatMap((r) => r.createdRctiIds || []),
    successes: executeResult.successes || [],
    failures: executeResult.failures || [],
    batchIds: commitResults.map((r) => r.batchId).filter(Boolean),
  };
};
